import React, { useState } from "react";
import Sidebar from "../../components/Sidebar/Sidebar";
import Footer from "../../components/Footer/Footer";
import { RefreshCcw, UploadCloud } from "lucide-react";
import { getFilesByUserId, uploadFile } from "../../api/filesApi";
import { useEffect } from "react";
import AllStorage from "../../components/Results/AllStorage";
import { getObservationsByCategory } from "../../api/observationsApi";

export default function Results() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [uploadedDocuments, setUploadedDocuments] = useState({
    "Lab Reports": [],
    "Imaging": [],
    "Prescriptions": []
  });

  const [sidebarExpanded, setSidebarExpanded] = useState(true)

  const [vitalsData, setVitalsData] = useState([]);

  const [labResults, setLabResults] = useState([]);

  const userId = localStorage.getItem('userId');

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const data = await getFilesByUserId(userId);

        // Always show all 3 categories even if empty
        const defaultStructure = {
          "Lab Reports": [],
          "Imaging": [],
          "Prescriptions": [],
          "Advance Directives": []
        };

        setUploadedDocuments({ ...defaultStructure, ...data });
      } catch (err) {
        console.error("Error fetching uploaded files:", err);
      }
    };

    fetchFiles();
  }, []);

  useEffect(() => {
  const patientId = localStorage.getItem("patientId");
  if (!patientId) {
    console.error("No patient ID found in localStorage");
    return;
  }

  const fetchPatientData = async () => {
    try {
      // Fetch vitals from your DB
      const vitals = await getObservationsByCategory(patientId, "vital");
      
      // Fetch labs from your DB
      const labs = await getObservationsByCategory(patientId, "lab");

      setVitalsData(vitals);
      setLabResults(labs);

    } catch (error) {
      console.error("Error fetching observations from DB:", error);
    }
  };

  fetchPatientData();
}, []);

  const handleRefresh = () => {
    const newVitals = [
      { date: "2025-03-10", bp: "117/76", weight: "147 lbs", pulse: 69, temp: "98.2°F" },
      { date: "2025-04-10", bp: "115/75", weight: "146 lbs", pulse: 68, temp: "98.3°F" },
    ];

    const newLabResults = [
      { test: "Hemoglobin A1C", value: "5.5%", date: "Jul 2025" },
      { test: "Cholesterol", value: "185 mg/dL", date: "Jul 2025" },
    ];

    setVitalsData(newVitals);
    setLabResults(newLabResults);

    alert("Health data refreshed!");
  };

  const handleFileUpload = async () => {
    try {
      const response = await uploadFile(selectedFile, selectedCategory, userId);
      console.log("File uploaded successfully:", response);

      const newDoc = {
        name: response.originalname,
        date: new Date().toLocaleDateString(),
        category: selectedCategory,
        file_url: response.file_url
      };

      setUploadedDocuments(prev => ({
        ...prev,
        [selectedCategory]: [...prev[selectedCategory], newDoc],
      }));

      // Reset input fields
      setSelectedFile(null);
      setSelectedCategory('');
    } catch (error) {
      console.error("File upload failed:", error);
    }
  };

  return (
    <div className={`ml-0 flex flex-col min-h-screen transition-all duration-300 ${sidebarExpanded ? 'md:ml-56' : 'md:ml-16'}`}>
      <Sidebar expanded={sidebarExpanded} setExpanded={setSidebarExpanded} />

      <main className="flex-1 p-6 overflow-auto bg-gray-100">
        <div className="px-2 py-6 mx-auto">
          <h1 className="mb-6 text-3xl font-bold text-[#04395b]">Results & Records</h1>

          <section className="mb-10">
            <h2 className="mb-4 text-2xl font-semibold">Connect with External Platforms</h2>
            <ol className="pl-5 space-y-2 list-decimal">
              <li>Open the app settings and navigate to "Connected Services".</li>
              <li>Select a provider like LabCorp, SimonMed, etc.</li>
              <li>Follow the on-screen instructions to authorize access.</li>
              <li>Once connected, your records will begin syncing.</li>
            </ol>
            <button onClick={handleRefresh} className="mt-4 flex items-center gap-2 px-4 py-2 text-sm text-white bg-[#04395b] rounded hover:bg-[#022b42]">
              <RefreshCcw size={16} /> Refresh Data
            </button>
          </section>

          {/* results overview */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold mb-6">Health Records Overview</h2>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="p-6 bg-white rounded-2xl shadow-md border border-gray-100">
                <h3 className="mb-4 text-lg font-semibold text-gray-800">Latest Lab Results</h3>
                <ul className="space-y-2">
                  {labResults.length == 0 ? (
                    <li className="text-sm text-gray-700">No lab results available.</li>
                  ) : (
                    labResults.map((lab) => (
                      <li key={lab.label + lab.date} className="flex justify-between text-sm text-gray-700">
                        <span className="font-medium">{lab.display_name}</span>
                        <span>{lab.value} {lab.unit} ({new Date(lab.effective_date).toLocaleDateString()})</span>
                      </li>
                    ))
                  )}
                </ul>
              </div>
              <div className="p-6 bg-white rounded-2xl shadow-md border border-gray-100">
                <h3 className="mb-4 text-lg font-semibold text-gray-800">Vitals Tracker</h3>
                <ul className="space-y-2">
                  {vitalsData.length == 0 ? (
                    <li className="text-sm text-gray-700">No vitals data available.</li>
                  ) : (
                    vitalsData.map((vital) => (
                      <li key={vital.label + vital.date} className="flex justify-between text-sm text-gray-700">
                        <span className="font-medium">{vital.display_name}</span>
                        <span>{vital.value}{vital.unit} ({new Date(vital.effective_date).toLocaleDateString()})</span>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          </section>


          {/*upload documents function */}
          <section className="mb-10">
            <h2 className="mb-4 text-2xl font-semibold">Upload Health Documents</h2>
            <div className="relative p-4 border-2 border-dashed rounded bg-gray-50 mb-2">
              <input
                type="file"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />

              <div className="flex items-center gap-2 text-sm text-[#04395b] z-0">
                <UploadCloud size={20} />
                <span className="font-medium">Select or drag files here</span>
              </div>
            </div>

            {selectedFile && (
              <p className="text-sm text-gray-700">Selected: {selectedFile.name}</p>
            )}

            <div className="mb-2">
              <select
                className="px-3 py-2 border rounded w-1/2"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">Select category</option>
                <option value="Lab Reports">Lab Reports</option>
                <option value="Imaging">Imaging</option>
                <option value="Prescriptions">Prescriptions</option>
                <option value="Advance Directives">Advance Directives</option>
              </select>
            </div>

            <button
              disabled={!selectedFile || !selectedCategory}
              onClick={handleFileUpload}
              className="px-3 py-2 bg-[#04395b] text-white rounded hover:bg-[#022b42] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Upload
            </button>
          </section>

          <AllStorage uploadedDocuments={uploadedDocuments} />

        </div>
      </main>
      <Footer />
    </div>
  );
}
